import { useEffect, useState } from 'hono/jsx';
import { SiteShell } from '../../components/SiteShell';
import { Spinner } from '../../components/Spinner';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { useSite } from '../../lib/site';

interface Plan {
  id: string;
  name: string;
  description?: string;
  interval: string;
  setupFee: number;
  subscription: number;
  setupFeeFormatted: string;
  subscriptionFormatted: string;
}

interface PaymentLink {
  url: string;
  plan: { name: string; total: number };
  expiresAt: string;
}

export function SiteBilling({ siteId }: { siteId: string }) {
  const { site, loading: siteLoading } = useSite(siteId);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [affiliateCode, setAffiliateCode] = useState<string>('');
  const [checkingOut, setCheckingOut] = useState<boolean>(false);
  const [linkPlan, setLinkPlan] = useState<string | null>(null);
  const [generating, setGenerating] = useState<boolean>(false);
  const [generatedLink, setGeneratedLink] = useState<PaymentLink | null>(null);

  async function loadPlans() {
    setLoading(true);
    try {
      const r = await api('/billing/plans');
      if (!r.ok) throw new Error('Failed to load plans');
      const data = await r.json();
      const list: Plan[] = data.plans || [];
      setPlans(list);
      if (list.length > 0) setSelectedPlan(list[0].id);
    } catch (e) {
      setPlans([]);
    } finally { setLoading(false); }
  }

  useEffect(() => { loadPlans(); }, [siteId]);

  async function startCheckout() {
    if (!selectedPlan) return;
    setCheckingOut(true);
    try {
      const r = await api('/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          planId: selectedPlan,
          affiliateCode: affiliateCode.trim() || undefined,
        }),
      });
      const result = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((result as any).error || 'Failed to create checkout');
      window.location.href = (result as any).checkoutUrl;
    } catch (err: any) {
      showToast('error', 'Checkout Error', err?.message || 'Checkout failed');
      setCheckingOut(false);
    }
  }

  async function generatePaymentLink() {
    if (!linkPlan) return;
    setGenerating(true);
    try {
      const r = await api('/sites/' + siteId + '/payment-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId: linkPlan }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((data as any).error || 'Failed to generate payment link');
      setGeneratedLink((data as any).paymentLink);
      showToast('success', 'Link Generated', 'Payment link is ready to share');
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to generate payment link');
    } finally { setGenerating(false); }
  }

  async function copyLink() {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink.url);
      showToast('success', 'Copied', 'Payment link copied to clipboard');
    } catch {
      showToast('error', 'Error', 'Could not copy to clipboard');
    }
  }

  return (
    <SiteShell site={site} loading={siteLoading} active="billing">
      <div class="space-y-6">
        {site?.subscription && (
          <div class="bg-dark-800 border border-emerald-500/20 rounded-xl p-6">
            <h2 class="text-lg font-semibold text-slate-100 mb-3">Current Plan</h2>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class={`w-2 h-2 rounded-full ${site.subscription.status === 'active' ? 'bg-emerald-400' : 'bg-amber-400'}`}></span>
                <span class="text-slate-200">{site.subscription.planName}</span>
              </div>
              <span class={`text-xs px-2 py-0.5 rounded ${site.subscription.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                {site.subscription.status === 'active' ? 'Active' : 'Past Due'}
              </span>
            </div>
          </div>
        )}

        <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-6">
          <h2 class="text-lg font-semibold text-slate-100 mb-1">Purchase a plan</h2>
          <p class="text-sm text-slate-400 mb-4">Pick a plan to pay for this site.</p>
          {loading ? (
            <div class="flex justify-center py-6"><Spinner /></div>
          ) : plans.length === 0 ? (
            <div class="text-center text-red-400 py-6">Failed to load pricing plans</div>
          ) : (
            <div class="space-y-3">
              {plans.map((p) => (
                <label class="block cursor-pointer">
                  <input type="radio" name="plan" value={p.id} checked={selectedPlan === p.id} onChange={() => setSelectedPlan(p.id)} class="hidden peer" />
                  <div class="p-4 rounded-lg border-2 border-slate-600/50 peer-checked:border-cyan-500 peer-checked:bg-cyan-500/10 hover:border-slate-500 transition-colors">
                    <div class="flex justify-between items-start">
                      <div>
                        <h4 class="font-medium text-slate-200">{p.name}</h4>
                        <p class="text-sm text-slate-400 mt-1">{p.description || ''}</p>
                      </div>
                      <div class="text-right">
                        <div class="text-lg font-bold text-slate-100">{p.setupFeeFormatted}</div>
                        <div class="text-xs text-slate-400">+ {p.subscriptionFormatted}</div>
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
          <div class="mt-4">
            <input
              type="text" placeholder="Affiliate code (optional)"
              value={affiliateCode}
              onInput={(e: Event) => setAffiliateCode((e.currentTarget as HTMLInputElement).value)}
              class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
            />
          </div>
          <button
            type="button" onClick={startCheckout} disabled={!selectedPlan || checkingOut}
            class="w-full mt-4 bg-cyan-500 text-dark-900 py-3 rounded-lg font-medium hover:bg-cyan-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >{checkingOut ? 'Redirecting…' : 'Continue to Checkout'}</button>
          <p class="text-xs text-slate-500 text-center mt-3">Secure payment powered by Stripe</p>
        </div>

        <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-6">
          <h2 class="text-lg font-semibold text-slate-100 mb-1">Payment link</h2>
          <p class="text-sm text-slate-400 mb-4">Generate a shareable link to collect payment from a customer.</p>
          {!generatedLink ? (
            <>
              <div class="space-y-2">
                {plans.map((p) => (
                  <label class="block">
                    <input type="radio" name="linkPlan" value={p.id} checked={linkPlan === p.id} onChange={() => setLinkPlan(p.id)} class="sr-only peer" />
                    <div class="p-3 rounded-lg border border-slate-600/50 cursor-pointer hover:border-cyan-500/50 peer-checked:border-cyan-500 peer-checked:bg-cyan-500/10 transition-colors">
                      <div class="flex justify-between items-start">
                        <div>
                          <p class="font-medium text-slate-200">{p.name}</p>
                          <p class="text-xs text-slate-400">{p.description || ''}</p>
                        </div>
                        <div class="text-right">
                          <p class="text-cyan-400 font-semibold">${(p.setupFee + p.subscription).toLocaleString()}</p>
                          <p class="text-xs text-slate-500">${p.setupFee} + ${p.subscription}/{p.interval}</p>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <button
                type="button" onClick={generatePaymentLink} disabled={!linkPlan || generating}
                class="w-full mt-4 bg-dark-700 border border-slate-600/50 text-slate-200 py-2.5 rounded-lg font-medium hover:bg-dark-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >{generating ? 'Generating…' : 'Generate Link'}</button>
            </>
          ) : (
            <div>
              <div class="bg-dark-900 rounded-lg p-4 mb-4">
                <p class="text-xs text-slate-500 mb-2">Share this link with your customer:</p>
                <div class="flex gap-2">
                  <input type="text" readOnly value={generatedLink.url} class="flex-1 px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-cyan-400 focus:outline-none" />
                  <button type="button" onClick={copyLink} class="px-3 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                  </button>
                </div>
              </div>
              <div class="text-sm text-slate-400 space-y-1">
                <p><strong class="text-slate-300">Plan:</strong> {generatedLink.plan.name}</p>
                <p><strong class="text-slate-300">Total:</strong> <span class="text-cyan-400">${generatedLink.plan.total.toLocaleString()}</span></p>
                <p><strong class="text-slate-300">Expires:</strong> {new Date(generatedLink.expiresAt).toLocaleDateString()}</p>
              </div>
              <button type="button" onClick={() => { setGeneratedLink(null); setLinkPlan(null); }} class="w-full mt-4 bg-dark-700 text-slate-200 py-2.5 rounded-lg font-medium hover:bg-dark-600 transition-colors">
                Generate Another
              </button>
            </div>
          )}
        </div>
      </div>
    </SiteShell>
  );
}
